import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";

import type { PopoverContentProps } from "@frontend/components/atoms/Popover";

import { ContactPreviewHost } from "./ContactPreviewHost";
import {
  ContactPreviewPopoverTrigger,
  type ContactPreviewPopoverTriggerProps,
} from "./ContactPreviewPopoverTrigger";
import { HOVER_SWITCH_DELAY_MS } from "./constants";
import {
  ContactPreviewTriggerContext,
  closeContact,
  getGroup,
  registerTrigger,
  requestContact,
  subscribeGroup,
} from "./contactPreviewGroup";

export type ContactPreviewPopoverProps = Pick<
  ContactPreviewPopoverTriggerProps,
  "label" | "avatarUrl" | "highlighted"
> & {
  readonly contactId: number;
  readonly side?: PopoverContentProps["side"];
  readonly children?: React.ReactNode;
};

export function ContactPreviewPopover({
  contactId,
  side = "left",
  label,
  avatarUrl,
  highlighted,
  children,
}: ContactPreviewPopoverProps) {
  const triggerId = useId();

  useEffect(
    () => registerTrigger({ triggerId, contactId }),
    [triggerId, contactId],
  );

  const group = useSyncExternalStore(subscribeGroup, getGroup);
  const shown = group.target?.triggerId === triggerId && group.open;
  const asked = group.pending?.triggerId === triggerId;

  const timer = useRef<number | undefined>(undefined);
  const cancelHover = useCallback(() => {
    window.clearTimeout(timer.current);
  }, []);

  useEffect(() => cancelHover, [cancelHover]);

  const context = useMemo(
    () => ({
      triggerId,
      contactId,
      loading: asked,
      cardOpen: group.open,
      activate: () => {
        cancelHover();

        if (shown || asked) {
          closeContact();
          return;
        }

        requestContact({ triggerId, contactId, side });
      },
      onPointerEnter: (pointerType: string) => {
        if (!group.open || shown || asked || pointerType !== "mouse") {
          return;
        }

        cancelHover();
        timer.current = window.setTimeout(() => {
          requestContact({ triggerId, contactId, side });
        }, HOVER_SWITCH_DELAY_MS);
      },
      onPointerLeave: cancelHover,
    }),
    [triggerId, contactId, side, shown, asked, group.open, cancelHover],
  );

  return (
    <ContactPreviewTriggerContext.Provider value={context}>
      {children ?? (
        <ContactPreviewPopoverTrigger
          label={label}
          avatarUrl={avatarUrl}
          highlighted={highlighted}
        />
      )}

      {group.hostId === triggerId && <ContactPreviewHost />}
    </ContactPreviewTriggerContext.Provider>
  );
}
